import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { fetchLeaderboards, fetchTopPerformers, fetchPlayerGameLogs } from '../../api/playerMetricsDashboard';
import PlayerDashboardHeader from './PlayerDashboardHeader';
import LeaderboardTable from './LeaderboardTable';
import TeamBattingChart from './TeamBattingChart';
import TeamPitchingChart from './TeamPitchingChart';
import PlayerRosterGrid from './PlayerRosterGrid';
import HotColdTracker from './HotColdTracker';
import PlayerProfile from '../PlayerProfile';

const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'batting', label: 'Batting' },
  { id: 'pitching', label: 'Pitching' },
  { id: 'roster', label: 'Roster' },
  { id: 'hotcold', label: 'Hot/Cold' },
];

const PlayerPerformanceDashboard = () => {
  const [leaderboards, setLeaderboards] = useState(null);
  const [performers, setPerformers] = useState(null);
  const [gameLogs, setGameLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedPlayer, setSelectedPlayer] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [lbData, perfData] = await Promise.all([
        fetchLeaderboards(111),
        fetchTopPerformers(111),
      ]);
      setLeaderboards(lbData);
      setPerformers(perfData);

      const rosterIds = (perfData?.roster?.roster || []).slice(0, 20).map(p => p.person?.id).filter(Boolean);
      if (rosterIds.length) {
        const logs = await fetchPlayerGameLogs(rosterIds);
        setGameLogs(logs);
      }
      setLastUpdated(new Date());
    } catch (e) {
      console.error('Player dashboard fetch failed', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handlePlayerClick = (id, name) => setSelectedPlayer({ id, name });

  return (
    <>
      <div className="border-2 border-[#658d71] bg-[rgba(0,0,0,0.05)] w-full">
        <div className="p-3 sm:p-5 border-b-2 border-[#658d71]">
          <PlayerDashboardHeader loading={loading} lastUpdated={lastUpdated} onRefresh={load} />
        </div>

        <div className="flex border-b-2 border-[#658d71] overflow-x-auto">
          {TABS.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2.5 text-[10px] sm:text-xs font-bold uppercase tracking-widest whitespace-nowrap border-r border-[#658d71] last:border-0 transition-colors ${activeTab === tab.id ? 'bg-[#386646] text-[#d8ceb3]' : 'text-[#a9c9b1] hover:text-white hover:bg-[rgba(255,255,255,0.04)]'}`}>
              {tab.label}
            </button>
          ))}
        </div>

        <div className="p-3 sm:p-5">
          {loading ? (
            <div className="flex items-center justify-center py-16 gap-3">
              <div className="w-7 h-7 border-2 border-[#658d71] border-t-[#d8ceb3] rounded-full animate-spin" />
              <span className="text-[#a9c9b1] text-xs tracking-widest uppercase">Loading player data...</span>
            </div>
          ) : (
            <AnimatePresence mode="wait">
              <motion.div key={activeTab} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.18 }} className="flex flex-col gap-4">
                {activeTab === 'overview' && (
                  <>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                      <LeaderboardTable leaderboards={leaderboards} onPlayerClick={handlePlayerClick} />
                      <HotColdTracker roster={performers?.roster?.roster} gameLogs={gameLogs} onPlayerClick={handlePlayerClick} />
                    </div>
                    <TeamBattingChart leaderboards={leaderboards} />
                  </>
                )}
                {activeTab === 'batting' && <TeamBattingChart leaderboards={leaderboards} />}
                {activeTab === 'pitching' && <TeamPitchingChart leaderboards={leaderboards} />}
                {activeTab === 'roster' && (
                  <PlayerRosterGrid roster={performers?.roster?.roster} gameLogs={gameLogs} onPlayerClick={handlePlayerClick} />
                )}
                {activeTab === 'hotcold' && (
                  <HotColdTracker roster={performers?.roster?.roster} gameLogs={gameLogs} onPlayerClick={handlePlayerClick} />
                )}
              </motion.div>
            </AnimatePresence>
          )}
        </div>
      </div>

      {selectedPlayer && (
        <PlayerProfile playerId={selectedPlayer.id} playerName={selectedPlayer.name} onClose={() => setSelectedPlayer(null)} />
      )}
    </>
  );
};

export default PlayerPerformanceDashboard;